/**
 * Life4Earth V2 — React hook for loading community metadata from the API.
 *
 * Fetches all 8 community/collection tables in parallel, converts to seed shapes,
 * and falls back to static seed data when the API is unreachable.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchAllCommunityEntitiesAdmin,
  fetchAllRelationships,
  fetchAllCollections,
  fetchAllCollectionProjects,
  fetchAllCustomEntities,
  fetchAllSchemas,
  fetchAllSchemaFields,
  fetchAllMetadataValues,
  fetchAllHierarchies,
  communityEntityToSeedShape,
  relationshipToSeedShape,
  collectionToSeedShape,
  collectionProjectToSeedShape,
  customEntityToSeedShape,
  schemaToSeedShape,
  schemaFieldToSeedShape,
  metadataValueToSeedShape,
} from "./communityMetadata";
import type {
  CommunityEntityResponse,
  CommunityEntityRelationshipResponse,
  CollectionResponse,
  CollectionProjectResponse,
  CustomEntityResponse,
  CollectionSchemaResponse,
  CollectionSchemaFieldResponse,
  ProjectCustomMetadataValueResponse,
  SchemaHierarchyResponse,
} from "./communityMetadata";
import { fetchForKey, CACHE_UPDATE_EVENT, QUERY_KEYS } from "./queryCache";

export interface UseApiCommunityDataResult {
  /** Community entities in seed shape */
  communityEntities: Record<string, any>[];
  /** Community entity relationships in seed shape */
  communityRelationships: Record<string, any>[];
  /** Collections in seed shape */
  collections: Record<string, any>[];
  /** Collection-project links in seed shape */
  collectionProjects: Record<string, any>[];
  /** Custom entities (Rotary hierarchy) in seed shape */
  customEntities: Record<string, any>[];
  /** Collection schemas in seed shape */
  schemas: Record<string, any>[];
  /** Schema fields in seed shape */
  schemaFields: Record<string, any>[];
  /** Project custom metadata values in seed shape */
  metadataValues: Record<string, any>[];
  /**
   * Schema hierarchies (DEV-920 Layer 2). Returned in API shape — no seed
   * mapping needed, since consumers parse `levelOrder` directly. Replaces
   * the hardcoded HIERARCHY_GROUP_OPTIONS / rotary-key sniffing patterns
   * in KpiGridTable and ProjectConfigModal.
   */
  hierarchies: SchemaHierarchyResponse[];

  /** Raw API responses for dashboard row counts */
  rawCounts: {
    communityEntities: number;
    relationships: number;
    collections: number;
    collectionProjects: number;
    customEntities: number;
    schemas: number;
    schemaFields: number;
    metadataValues: number;
    hierarchies: number;
  };

  loading: boolean;
  error: string | null;
  source: "api" | "seed" | "loading";
  refetch: () => void;
}

export interface CommunityFallbacks {
  communityEntities?: () => any[];
  communityRelationships?: () => any[];
  collections?: () => any[];
  collectionProjects?: () => any[];
  customEntities?: () => any[];
  schemas?: () => any[];
  schemaFields?: () => any[];
  metadataValues?: () => any[];
  hierarchies?: () => SchemaHierarchyResponse[];
}

export function useApiCommunityData(
  fallbacks?: CommunityFallbacks
): UseApiCommunityDataResult {
  const mountedRef = useRef(true);
  const [data, setData] = useState<UseApiCommunityDataResult>({
    communityEntities: [],
    communityRelationships: [],
    collections: [],
    collectionProjects: [],
    customEntities: [],
    schemas: [],
    schemaFields: [],
    metadataValues: [],
    hierarchies: [],
    rawCounts: {
      communityEntities: 0, relationships: 0, collections: 0,
      collectionProjects: 0, customEntities: 0, schemas: 0,
      schemaFields: 0, metadataValues: 0, hierarchies: 0,
    },
    loading: true,
    error: null,
    source: "loading",
    refetch: () => {},
  });

  const refetch = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      const cached = await fetchForKey(
        QUERY_KEYS.community,
        async () => {
          const [
            apiEntities, apiRels, apiColls, apiCps,
            apiCustom, apiSchemas, apiFields, apiValues, apiHierarchies,
          ] = await Promise.all([
            fetchAllCommunityEntitiesAdmin(),
            fetchAllRelationships(),
            fetchAllCollections(),
            fetchAllCollectionProjects(),
            fetchAllCustomEntities(),
            fetchAllSchemas(),
            fetchAllSchemaFields(),
            fetchAllMetadataValues(),
            fetchAllHierarchies(),
          ]);

          return {
            communityEntities: apiEntities.map(communityEntityToSeedShape),
            communityRelationships: apiRels.map(relationshipToSeedShape),
            collections: apiColls.map(collectionToSeedShape),
            collectionProjects: apiCps.map(collectionProjectToSeedShape),
            customEntities: apiCustom.map(customEntityToSeedShape),
            schemas: apiSchemas.map(schemaToSeedShape),
            schemaFields: apiFields.map(schemaFieldToSeedShape),
            metadataValues: apiValues.map(metadataValueToSeedShape),
            hierarchies: apiHierarchies,
            rawCounts: {
              communityEntities: apiEntities.length,
              relationships: apiRels.length,
              collections: apiColls.length,
              collectionProjects: apiCps.length,
              customEntities: apiCustom.length,
              schemas: apiSchemas.length,
              schemaFields: apiFields.length,
              metadataValues: apiValues.length,
              hierarchies: apiHierarchies.length,
            },
          };
        },
        {
          force: true,
          fallbackFn: () => {
            const tryFallback = (fn?: () => any[]) => {
              if (fn) { try { return fn(); } catch { return []; } }
              return [];
            };

            return {
              communityEntities: tryFallback(fallbacks?.communityEntities),
              communityRelationships: tryFallback(fallbacks?.communityRelationships),
              collections: tryFallback(fallbacks?.collections),
              collectionProjects: tryFallback(fallbacks?.collectionProjects),
              customEntities: tryFallback(fallbacks?.customEntities),
              schemas: tryFallback(fallbacks?.schemas),
              schemaFields: tryFallback(fallbacks?.schemaFields),
              metadataValues: tryFallback(fallbacks?.metadataValues),
              hierarchies: tryFallback(fallbacks?.hierarchies) as SchemaHierarchyResponse[],
              rawCounts: {
                communityEntities: 0, relationships: 0, collections: 0,
                collectionProjects: 0, customEntities: 0, schemas: 0,
                schemaFields: 0, metadataValues: 0, hierarchies: 0,
              },
            };
          },
        }
      );

      if (mountedRef.current) {
        setData({
          communityEntities: cached.communityEntities,
          communityRelationships: cached.communityRelationships,
          collections: cached.collections,
          collectionProjects: cached.collectionProjects,
          customEntities: cached.customEntities,
          schemas: cached.schemas,
          schemaFields: cached.schemaFields,
          metadataValues: cached.metadataValues,
          hierarchies: cached.hierarchies,
          rawCounts: cached.rawCounts,
          loading: false,
          error: null,
          source: "api",
          refetch,
        });
      }
    } catch (err: any) {
      if (mountedRef.current) {
        setData({
          communityEntities: [],
          communityRelationships: [],
          collections: [],
          collectionProjects: [],
          customEntities: [],
          schemas: [],
          schemaFields: [],
          metadataValues: [],
          hierarchies: [],
          rawCounts: {
            communityEntities: 0, relationships: 0, collections: 0,
            collectionProjects: 0, customEntities: 0, schemas: 0,
            schemaFields: 0, metadataValues: 0, hierarchies: 0,
          },
          loading: false,
          error: err.message,
          source: "seed",
          refetch,
        });
      }
    }
  }, [fallbacks]);

  useEffect(() => {
    mountedRef.current = true;

    async function load() {
      try {
        const cached = await fetchForKey(
          QUERY_KEYS.community,
          async () => {
            const [
              apiEntities, apiRels, apiColls, apiCps,
              apiCustom, apiSchemas, apiFields, apiValues, apiHierarchies,
            ] = await Promise.all([
              fetchAllCommunityEntitiesAdmin(),
              fetchAllRelationships(),
              fetchAllCollections(),
              fetchAllCollectionProjects(),
              fetchAllCustomEntities(),
              fetchAllSchemas(),
              fetchAllSchemaFields(),
              fetchAllMetadataValues(),
              fetchAllHierarchies(),
            ]);

            return {
              communityEntities: apiEntities.map(communityEntityToSeedShape),
              communityRelationships: apiRels.map(relationshipToSeedShape),
              collections: apiColls.map(collectionToSeedShape),
              collectionProjects: apiCps.map(collectionProjectToSeedShape),
              customEntities: apiCustom.map(customEntityToSeedShape),
              schemas: apiSchemas.map(schemaToSeedShape),
              schemaFields: apiFields.map(schemaFieldToSeedShape),
              metadataValues: apiValues.map(metadataValueToSeedShape),
              hierarchies: apiHierarchies,
              rawCounts: {
                communityEntities: apiEntities.length,
                relationships: apiRels.length,
                collections: apiColls.length,
                collectionProjects: apiCps.length,
                customEntities: apiCustom.length,
                schemas: apiSchemas.length,
                schemaFields: apiFields.length,
                metadataValues: apiValues.length,
                hierarchies: apiHierarchies.length,
              },
            };
          },
          {
            fallbackFn: () => {
              const tryFallback = (fn?: () => any[]) => {
                if (fn) { try { return fn(); } catch { return []; } }
                return [];
              };

              return {
                communityEntities: tryFallback(fallbacks?.communityEntities),
                communityRelationships: tryFallback(fallbacks?.communityRelationships),
                collections: tryFallback(fallbacks?.collections),
                collectionProjects: tryFallback(fallbacks?.collectionProjects),
                customEntities: tryFallback(fallbacks?.customEntities),
                schemas: tryFallback(fallbacks?.schemas),
                schemaFields: tryFallback(fallbacks?.schemaFields),
                metadataValues: tryFallback(fallbacks?.metadataValues),
                hierarchies: tryFallback(fallbacks?.hierarchies) as SchemaHierarchyResponse[],
                rawCounts: {
                  communityEntities: 0, relationships: 0, collections: 0,
                  collectionProjects: 0, customEntities: 0, schemas: 0,
                  schemaFields: 0, metadataValues: 0, hierarchies: 0,
                },
              };
            },
          }
        );

        if (mountedRef.current) {
          setData({
            communityEntities: cached.communityEntities,
            communityRelationships: cached.communityRelationships,
            collections: cached.collections,
            collectionProjects: cached.collectionProjects,
            customEntities: cached.customEntities,
            schemas: cached.schemas,
            schemaFields: cached.schemaFields,
            metadataValues: cached.metadataValues,
            hierarchies: cached.hierarchies,
            rawCounts: cached.rawCounts,
            loading: false,
            error: null,
            source: "api",
            refetch,
          });
        }
      } catch (err: any) {
        if (mountedRef.current) {
          setData({
            communityEntities: [],
            communityRelationships: [],
            collections: [],
            collectionProjects: [],
            customEntities: [],
            schemas: [],
            schemaFields: [],
            metadataValues: [],
            hierarchies: [],
            rawCounts: {
              communityEntities: 0, relationships: 0, collections: 0,
              collectionProjects: 0, customEntities: 0, schemas: 0,
              schemaFields: 0, metadataValues: 0, hierarchies: 0,
            },
            loading: false,
            error: err.message,
            source: "seed",
            refetch,
          });
        }
      }
    }

    load();

    const handleCacheUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.key === QUERY_KEYS.community) {
        load();
      }
    };

    window.addEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);
    };
  }, [refetch, fallbacks]);

  return data;
}
